<template>
  <div class="settings-forms-container m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <div>
        <b-button size="sm" @click="addForm(category)" variant="primary">
          New
        </b-button>
      </div>
      <b-form-group description="" label="" class="text-left mb-0">
        <b-form-input
          size="sm"
          placeholder="Search"
          v-model="search"
        ></b-form-input>
      </b-form-group>
    </div>
    <h5>
      Forms <span class="grid-count">({{ itemsFilter.length }} forms)</span>
    </h5>
    <grid
      class=""
      ref="grid"
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="items"
      :selectionMode="gridConfig.selectionMode"
      :columnLines="gridConfig.columnLines"
      :searchFeature="true"
      :stripeFeature="false"
      @storesort="listeners.storeSort"
    ></grid>
    <bottom-paginator
      :total="itemsFilter.length"
      :page="paginator.page"
      :perPage="paginator.perPage"
      @change-page="changePage"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { formatDate } from "../../helpers/date";
import Grid from "../../third_party/grid/Grid.vue";
import BottomPaginator from "../controls/BottomPaginator.vue";
import { randomId, isStandaloneOrNotCore } from "../../helpers/general";
import FormBuilderAddEdit from "./FormBuilderAddEdit.vue";
import formServer from "../../server/form";

export default {
  name: "FormList",
  components: {
    Grid,
    BottomPaginator
  },
  props: ["category"],
  data() {
    const self = this;
    return {
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: true
        },
        columnLines: false
      },
      paginator: {
        page: 0,
        perPage: 10
      },
      search: "",
      sortBy: undefined,
      listeners: {
        storeSort: ({ sorters }) => {
          self.sortBy = sorters[0];
          self.changePage(0);
        }
      }
    };
  },
  mounted() {
    this.fetchFormBuilderForms({ category: this.category });
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    ...mapActions("formBuilder", [
      "fetchFormBuilderForms",
      "deleteFormBuilderForm"
    ]),
    ...mapMutations("client", ["dispatchListener"]),
    addForm(category) {
      this.$emit("add-tab", {
        key: "form-builder-form-" + randomId(),
        text: "New Form",
        component: FormBuilderAddEdit,
        props: { category },
        closeable: true
      });
    },
    async deleteForm(form) {
      await this.deleteFormBuilderForm({
        id: form.id,
        category: form.category
      });
      this.dispatchListener({
        listener: "fetchDocumentMenus",
        param: "all"
      });
    },
    changePage(page) {
      this.paginator.page = page;
    }
  },
  computed: {
    ...mapState("formBuilder", {
      list: (state) => state.list,
      currentUser: (state) => state.currentUser
    }),
    ...mapState("layout", {
      advancedOptions: (state) => state.advancedOptions
    }),
    itemsFilter() {
      let list = this.list[this.category];

      return list
        .filter((item) => {
          const query = this.search.trim().toLowerCase();
          return query == "" || item.name.toLowerCase().indexOf(query) >= 0;
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    items() {
      let items = this.itemsFilter.slice();
      if (this.sortBy) {
        const order = this.sortBy.ascending ? 1 : -1;
        items.sort((a, b) => {
          const propA = a[this.sortBy.field];
          const propB = b[this.sortBy.field];

          const valueA =
            this.sortBy.field == "createdBy"
              ? propA
                ? this.formatUser(propA)
                : ""
              : propA;
          const valueB =
            this.sortBy.field == "createdBy"
              ? propB
                ? this.formatUser(propB)
                : ""
              : propB;

          return (
            order * valueA.toLowerCase().localeCompare(valueB.toLowerCase())
          );
        });
      }

      return items.slice(
        this.paginator.page * this.paginator.perPage,
        (this.paginator.page + 1) * this.paginator.perPage
      );
    },
    columns() {
      let columns = [];
      columns.push({
        text: "Name",
        field: "name",
        flex: 1,
        editor: false
      });
      columns.push({
        text: "Created on",
        field: "createdAt",
        type: "template",
        width: 170,
        editor: false,
        template: (data) => formatDate(data.record.data.createdAt)
      });
      columns.push({
        text: "Created by",
        field: "createdBy",
        type: "template",
        width: 250,
        editor: false,
        template: ({ record }) =>
          record.createdBy ? this.formatUser(record.createdBy) : ""
      });
      columns.push({
        text: "Tools",
        type: "action",
        field: "action",
        align: "right",
        width: 80,
        actions: [
          {
            tooltip: "Edit",
            renderer: () =>
              `<img class="b-action-item" src="${
                isStandaloneOrNotCore()
                  ? `/icons/edit.svg`
                  : `/desktop/icons/edit.svg`
              }">`,
            onClick: async ({ record }) => {
              let updated = await formServer.updateLastTimeEditing(record.id);
              if (!updated) {
                let form = await formServer.get(record.id);
                this.showError({
                  message: `You can not edit this form at the moment because it's being edited by user: ${form.lastTimeEditingUser.fullName}. Please try again in a few minutes.`
                });
              } else {
                this.$emit("add-tab", {
                  key: "form-builder-form-" + record.id,
                  text: record.name,
                  component: FormBuilderAddEdit,
                  props: { id: record.id, category: record.data.category },
                  closeable: true
                });
              }
            }
          },
          {
            tooltip: "Delete",
            renderer: () =>
              `<img class="b-action-item ml-2 mr-1" src="${
                isStandaloneOrNotCore()
                  ? `/icons/delete.svg`
                  : `/desktop/icons/delete.svg`
              }">`,
            onClick: ({ record }) => {
              this.$dialog
                .confirm(
                  {
                    title: "Are you sure you want to delete this record?"
                  },
                  {
                    view: "confirm-delete"
                  }
                )
                .then(async () => {
                  await this.deleteForm(record);
                  this.showAlertSuccess();
                })
                .catch(function () {});
            }
          }
        ]
      });
      return columns;
    }
  },
  watch: {
    search: function () {
      this.paginator.page = 0;
    }
  }
};
</script>

<style scoped>
.grid-count {
  color: #e0e0e0;
  font-size: 0.8rem;
}
</style>

<style>
.settings-forms-container .b-grid-container .b-container {
  max-height: calc(100vh - 300px) !important;
}
</style>
